import { useRef, useState } from "react";
import styles from "./TestimonialForm.module.css";
import Icon from "./Icons";
import { submitTestimonial } from "../data/firestore";
import { isFirebaseConfigured } from "../firebase";

const EMPTY = { name: "", context: "", area: "", quote: "", company: "" };

export default function TestimonialForm() {
  const [values, setValues] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const formRef = useRef(null);

  const set = (field) => (e) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.name.trim() || !values.quote.trim()) return;
    // honeypot: a real person never fills this hidden field
    if (values.company) return;

    setStatus("sending");
    try {
      await submitTestimonial(values);
      setStatus("sent");
      setValues(EMPTY);
    } catch {
      setStatus("error");
    }
  };

  if (!isFirebaseConfigured) return null;

  if (status === "sent") {
    return (
      <div className={styles.done} role="status">
        <Icon name="chevron" size={20} className={styles.doneIcon} />
        <div>
          <p className={styles.doneTitle}>Thank you.</p>
          <p className={styles.doneText}>
            Your testimonial is live on the page now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} ref={formRef}>
      <h3 className={styles.title}>Share your experience</h3>
      <p className={styles.note}>
        Posted publicly under the name you give. Keep it to what actually
        happened — no need to be flattering, just honest.
      </p>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="tm-name">
            Your name <span aria-hidden="true">*</span>
          </label>
          <input
            id="tm-name"
            value={values.name}
            onChange={set("name")}
            maxLength={60}
            required
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="tm-context">What did we treat?</label>
          <input
            id="tm-context"
            placeholder="e.g. Low back pain"
            value={values.context}
            onChange={set("context")}
            maxLength={80}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="tm-area">Your area (optional)</label>
        <input
          id="tm-area"
          placeholder="e.g. DLF Phase 3"
          value={values.area}
          onChange={set("area")}
          maxLength={60}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="tm-quote">
          Your testimonial <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="tm-quote"
          rows={4}
          value={values.quote}
          onChange={set("quote")}
          maxLength={600}
          required
        />
      </div>

      {/* honeypot — hidden from real visitors via CSS, bots fill every field */}
      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor="tm-company">Company</label>
        <input
          id="tm-company"
          tabIndex={-1}
          autoComplete="off"
          value={values.company}
          onChange={set("company")}
        />
      </div>

      <button type="submit" className={styles.submit} disabled={status === "sending"}>
        {status === "sending" ? "Posting…" : "Post testimonial"}
      </button>

      {status === "error" && (
        <p className={styles.error} role="alert">
          That didn't go through — check your connection and try again.
        </p>
      )}
    </form>
  );
}
